import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { NotificationsLevel } from "../../../lib/enums";
import { ErrorResponse, MessageErrorResponse } from "../../../requests/@types";
import { enums } from "../../../shim/jsbridge";
import { show } from "../../../notifications/notifications";
import api from "../api";
import { sampleReportKeys } from "../queryKeys";

const { state } = enums || {};

const DROPPED_FILES_POLLING_INTERVAL = 10 * 1000;

/**
 * Handles dropped files static analysis. Sends dropped files to static analysis and polls
 * api for result
 *
 * @param {string} hash hash value of parent sample
 * @param {string} analysisId analysisId of specific analysis
 *
 * @returns {Object} sendToStaticAnalysis used to initiate static analysis, isLoading indicates is currently loading
 */
const useDroppedFilesStaticAnalysis = (hash: string, analysisId: string) => {
  const [enabled, setEnabled] = useState(true);
  const queryClient = useQueryClient();

  const statusQueryKey =
    sampleReportKeys.droppedFilesStaticAnalysisStatus(analysisId);

  const { mutate, isPending } = useMutation<
    AxiosResponse<void>,
    ErrorResponse<MessageErrorResponse>
  >({
    mutationKey: sampleReportKeys.droppedFilesToStaticAnalysis(
      hash,
      analysisId
    ),
    mutationFn: () => api.sendDroppedFilesToStaticAnalysis(hash, analysisId),
    onMutate: () => {
      queryClient.setQueryData(statusQueryKey, {
        data: { status: state.PROCESSING }
      });
      show({
        level: NotificationsLevel.SUCCESS,
        message: `Dropped files for analysis: ${analysisId} sent to static analysis.`
      });
    },
    onSuccess: () => {
      setEnabled(true);
    },
    onError: ({ data: { message } }) => {
      queryClient.setQueryData(statusQueryKey, {
        data: { status: state.PROCESSED }
      });
      setEnabled(false);
      show({
        level: NotificationsLevel.ERROR,
        message: message || "There was an unexpected error."
      });
    }
  });

  const {
    data: { status: analysisStatus } = {},
    isLoading: isQueryLoading,
    isSuccess
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: statusQueryKey,
    queryFn: () => api.getDroppedFilesStaticAnalysisStatus(hash, analysisId),
    enabled,
    refetchInterval: DROPPED_FILES_POLLING_INTERVAL,
    select: ({ data }) => data
  });

  useEffect(() => {
    if (isSuccess) {
      setEnabled(analysisStatus === state.PROCESSING);
    }
  }, [isSuccess, analysisStatus]);

  const loading =
    isPending || isQueryLoading || analysisStatus === state.PROCESSING;

  return {
    sendToStaticAnalysis: mutate,
    isLoading: loading
  };
};

export default useDroppedFilesStaticAnalysis;
